import { Box, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useState, useEffect, useMemo } from "react";
import { useTheme } from "@mui/material";
import { cobotColours } from "../theme/theme";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import api from "../services/api";

const Users = ({ selectedFilter }) => {
    const cobotTheme = useTheme();
    const colors = cobotColours(cobotTheme.palette);
    const isMobileScreen = useMediaQuery('(max-width:768px)');
    
    const [employees, setEmployees] = useState([]);
    const [clockings, setClockings] = useState([]);
    const [breaks, setBreaks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const [empData, clockingsData, breaksData] = await Promise.all([
                    api.employee.getAll(),
                    api.shiftActivities.getAllClockings(),
                    api.shiftActivities.getAllBreaks()
                ]);

                setEmployees(empData.success ? empData.data : []);
                setClockings(clockingsData.success ? clockingsData.data : []);
                setBreaks(breaksData.success ? breaksData.data : []);

            } catch (err) {
                console.error("Error fetching data:", err);
                setError(err.message);
                setEmployees([]);
                setClockings([]);
                setBreaks([]);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const processedData = useMemo(() => {
        if (!employees.length && !loading) return [];

        return employees.map(emp => {
            const currentClocking = clockings.find(
                c => c.employeeId === emp._id && !c.clockOutTime
            );
            let currentBreak = null;
            if (currentClocking) {
                currentBreak = breaks.find(
                    b => b.clockingId === currentClocking._id && !b.breakEndTime
                );
            }

            let status = "Off Shift";
            let shiftDisplayTime = "";
            const now = moment();

            if (currentClocking) {
                status = currentBreak ? "On Break" : "Working";
                if (currentClocking.shiftId) { // shiftId is populated by backend in getAllClockings
                    const currentShift = currentClocking.shiftId;
                     shiftDisplayTime = `${moment(currentShift.startTime).format("HH:mm")} - ${moment(currentShift.endTime).format("HH:mm")}`;
                }
            } else if (emp.shifts && emp.shifts.length > 0) {
                const upcomingShift = emp.shifts.find(s => 
                    moment(s.startTime).isAfter(now) && 
                    moment(s.startTime).isBefore(moment(now).add(8, 'hours'))
                );
                if (upcomingShift) {
                    status = `Upcoming: ${moment(upcomingShift.startTime).format("HH:mm")}`;
                    shiftDisplayTime = `${moment(upcomingShift.startTime).format("HH:mm")} - ${moment(upcomingShift.endTime).format("HH:mm")} (Upcoming)`;
                }
            }

            return {
                ...emp,
                id: emp._id,
                displayName: `${emp.names.firstName} ${emp.names.lastName}`,
                status: status,
                displayShiftTime: shiftDisplayTime,
            };
        }).filter(emp => {
            if (selectedFilter === "All") return true;
            const empStatus = emp.status.toLowerCase();
            const filter = selectedFilter.toLowerCase();
            if (filter === "working" && empStatus === "working") return true;
            if (filter === "on break" && empStatus === "on break") return true;
            if (filter === "upcoming shift" && empStatus.startsWith("upcoming")) return true;
            return false;
        });
    }, [employees, clockings, breaks, selectedFilter, loading]);


    if (loading) return <Typography sx={{textAlign: 'center', mt: 5}}>Loading employee data...</Typography>;
    if (error) return <Typography sx={{textAlign: 'center', mt: 5, color: 'red'}}>Error: {error}</Typography>;
    if (!processedData.length && selectedFilter === "All") return <Typography sx={{textAlign: 'center', mt: 5}}>No employees found.</Typography>;
    if (!processedData.length) return <Typography sx={{textAlign: 'center', mt: 5}}>No employees on "{selectedFilter}".</Typography>;


    const columns = [
        {
            field: "profilePic",
            headerName: "",
            width: 80,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        borderRadius="50%"
                    width="50px"
                    height="50px"
                        backgroundColor={colors.primary.teal}
                    sx={{ml: 'auto', mr: 'auto'}}
                    >
                    <Typography fontWeight="bold" variant='h6' color={colors.text.white}>
                        {params.row.displayName.split(" ").map(n => n[0]).join("").toUpperCase()}
                        </Typography>
                    </Box>
            ),
        },
        { field: "displayName", headerName: "Name", flex: 1, cellClassName: "name-column--cell" },
        { field: "displayShiftTime", headerName: "Display Shift Time", flex: 1 },
        
    ];

    return (
        <Box>
            <Box
                m="10px 0 0 0"
                height="calc(76vh - 40px)" // Adjust height if filter bar takes space
                sx={{
                    "& .MuiDataGrid-root": { border: "none", backgroundColor: "transparent" },
                    "& .MuiDataGrid-row": { backgroundColor: colors.grey.lighter, borderBottom: `10px solid ${colors.primary.special}`, borderRadius: "5px", cursor: 'pointer', '&:hover': { backgroundColor: colors.grey.light } },
                    "& .MuiDataGrid-cell": { borderBottom: "none" },
                    "& .name-column--cell": { color: colors.primary.teal, fontWeight: "bold" },
                    "& .MuiDataGrid-columnHeaders": { backgroundColor: colors.grey.light, borderBottom: `2px solid ${colors.primary.teal}`, display: "none" },
                    "& .MuiDataGrid-virtualScroller": { backgroundColor: "transparent" },
                    "& .MuiDataGrid-footerContainer": { borderTop: `1px solid ${colors.grey.main}`, display: "flex" },
                    "& .MuiDataGrid-toolbarContainer .MuiButton-text": { color: `${colors.secondary.fire} !important`, fontSize: "16px" },
                }}
            >
                <DataGrid
                    rows={processedData}
                    columns={columns}
                    //slots={{ toolbar: GridToolbar }}
                    rowHeight={isMobileScreen ? 80 : 100}
                    onRowClick={(params) => navigate(`/LoginPage/${params.row.id}`, { state: { fromDashboard: true } })}
                />
            </Box>
        </Box>
    );
};

export default Users;
